import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
  bookmarkPayload,
  dryRun,
  getVersion,
  listBookmarks,
  type TDryRunInput,
  type TTransform,
} from '../../entities/transform';

export const SAMPLE_BODY = `{
  "data": {
    "object": {
      "id": "in_1Qk8Xz",
      "amount_paid": 4200,
      "customer": "cus_RdK3"
    }
  }
}`;

const INLINE = 'inline';

const decodeBody = (bodyB64: string): string => {
  try {
    const text = new TextDecoder().decode(
      Uint8Array.from(atob(bodyB64), (character) => character.charCodeAt(0)),
    );
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return '(the stored body is not JSON text)';
  }
};

export const useDryRun = (transform: TTransform, open: boolean) => {
  const [sample, setSample] = useState<string>(INLINE);
  const [version, setVersion] = useState<number>(transform.latestVersion);
  const [body, setBody] = useState(SAMPLE_BODY);

  const bookmarks = useQuery({
    queryKey: ['bookmarks'],
    queryFn: listBookmarks,
    enabled: open,
  });

  const chosen = bookmarks.data?.data.find((bookmark) => bookmark.id === sample) ?? null;

  const payload = useQuery({
    queryKey: ['bookmark-payload', chosen?.name],
    queryFn: () => bookmarkPayload(chosen?.name ?? ''),
    enabled: open && chosen !== null,
  });

  // The endpoint takes code, never a version number, so the chosen version has to be read back and posted.
  const source = useQuery({
    queryKey: ['transform-version', transform.id, version],
    queryFn: () => getVersion(transform.id, version),
    enabled: open,
  });

  const inlineError = useMemo(() => {
    if (chosen) return null;
    try {
      JSON.parse(body);
      return null;
    } catch (cause) {
      return cause instanceof Error ? cause.message : 'Not valid JSON';
    }
  }, [body, chosen]);

  const input: TDryRunInput | null = chosen
    ? { kind: 'bookmark', bookmarkId: chosen.id }
    : inlineError
      ? null
      : { kind: 'inline', request: { body: JSON.parse(body) as Record<string, unknown> } };

  const run = useMutation({
    mutationFn: () => {
      if (!input || !source.data) throw new Error('nothing to run');
      return dryRun(transform.id, input, source.data.code ?? undefined);
    },
  });

  return {
    sample,
    setSample: (next: string) => {
      setSample(next);
      run.reset();
    },
    version,
    setVersion,
    body,
    setBody,
    inlineError,
    bookmarks: bookmarks.data?.data ?? [],
    incoming: chosen ? (payload.data ? decodeBody(payload.data.request.bodyB64) : null) : body,
    incomingPending: chosen !== null && payload.isPending,
    source,
    run,
    canRun: input !== null && source.data !== undefined,
  };
};

export const INLINE_SAMPLE = INLINE;
